import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as PhasesService from '../services/PhasesService';
import { Phase } from '../types';
import { notifyError, notifySuccess } from '../../../components/PlatformNotification';

const DEFAULT_LIMIT = 10;

// Query keys
export const phasesKeys = {
    all: ['phases'] as const,
    lists: () => [...phasesKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...phasesKeys.lists(), { filters }] as const,
    details: () => [...phasesKeys.all, 'detail'] as const,
    detail: (id: string) => [...phasesKeys.details(), id] as const,
};

// Hooks
//
// `filters` carries the ENCLOSING PLAYBOOK ID when this list renders inside
// /playbooks/:id — see RecordsTable, which reads it from useParams. It is part
// of the queryKey via phasesKeys.list(), so navigating from one playbook to
// another refetches rather than serving the previous playbook's phases from
// cache. Omitting it would show playbook A's phases under playbook B.
export const usePhasesQuery = (filters: Record<string, string> = {}) => {
    return useInfiniteQuery({
        queryKey: phasesKeys.list(filters),
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            PhasesService.fetchRecords({
                filter: filters,
                limit: DEFAULT_LIMIT,
                nextToken: pageParam,
            }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
    });
};

export const usePhaseQuery = (id: string) => {
    return useQuery({
        queryKey: phasesKeys.detail(id),
        queryFn: () => PhasesService.fetch(id),
        enabled: !!id,
    });
};

export const useCreatePhaseMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: PhasesService.add,
        onSuccess: () => {
            notifySuccess('Phase created');
            queryClient.invalidateQueries({ queryKey: phasesKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdatePhaseMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: Phase }) =>
            PhasesService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Phase updated');
            queryClient.invalidateQueries({ queryKey: phasesKeys.lists() });
            queryClient.invalidateQueries({ queryKey: phasesKeys.detail(id) });
        },
        onError: notifyError,
    });
};

export const useDeletePhaseMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: PhasesService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Phase deleted');
            queryClient.invalidateQueries({ queryKey: phasesKeys.all });
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const usePhasesFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
